import Resource from 'Resources/Resource';
import EventBus from 'App/services/EventBus';
import config from 'App/babel/config';
import { globalTracker } from 'Helpers/Tracker';
import { modules } from 'App/helpers/segmentHelper';
import { STYLE } from 'Store/reducers/SystemNotificationReducer';
import { Feature, isAvailable } from 'App/services/FeatureHelperStandalone';
import { stripAngularInternals } from 'App/resources/ListView';

/*
	Standalone replacement for the write half of Tools.ReportView (WEB-11188). The ReportView twin
	of App/resources/ListView.ts (WEB-7834) — read that file first, it explains every decision
	repeated here.

	A 1:1 port of the save/delete paths of upsales/common/resources/reportView.js. Unlike ListView
	the Angular factory does NOT delegate here: no Angular caller writes report views, so there is
	still exactly one writer and reportView.* events cannot land twice.

	get/find are deliberately NOT ported: their only remaining consumer is
	upsales/common/resources/pushNotifications.js, which sits in the Angular tree, and reads cannot
	desync events.

	Notifications go through window.NotificationService rather than a static import of
	App/babel/NotificationService: that module imports the store, and this one is imported by
	Store/reducers/SharedViewsReducer, so the static import would close a require cycle through
	babel/store/index.ts.
*/

type ReportViewLike = {
	id?: number | string | null;
	type?: string;
	[key: string]: any;
};

export type ReportViewOptions = {
	skipEvent?: boolean;
	skipNotification?: boolean;
	skipErrorNotification?: boolean;
};

type ReportViewResponse = { data: any; metadata?: any };

const EVENT_PREFIX = 'reportView';

/*
	Reproduction of ResourceHelper.onSave/onSaveError/onDelete/onDeleteError
	(upsales/common/resources/resourceHelper.js) with the defaults reportView.js set at its lines
	14-22. Only the branches reportView can actually reach are kept: no caller passes an
	eventPrefix function, analyticsProps or notificationType, and the Angular resource called
	onSaveError / onDeleteError WITHOUT the error object, so ResourceHelper's parseError always
	returned null and the error body was always the constant below. Do not "improve" that into
	showing the server error — it would change what the toast says.

	The bodies are the listView ones: that is what reportView.js set, not a copy-paste slip here.
*/
const notify = (title: string, body: string, isError: boolean) => {
	window.NotificationService.add({
		title,
		body,
		style: isError ? STYLE.ERROR : STYLE.SUCCESS,
		icon: isError ? 'times' : 'check'
	});
};

/*
	EventBus is a facade over the real $rootScope, so upsales/common/services/appService.js:73/85/106
	still hears reportView.added / .updated / .deleted and refreshes the report-view store from them.
	Those listeners are load-bearing — do not add a second broadcast anywhere.
*/
const broadcast = (event: string, data: unknown) => {
	if (config.ENV === 'DEV' && console && console.log) {
		console.log('broadcasting:', `${EVENT_PREFIX}.${event}`);
	}
	EventBus.broadcast(`${EVENT_PREFIX}.${event}`, data);
};

const onSave = (options: ReportViewOptions, data: unknown, isNew: boolean) => {
	globalTracker.track(`${isNew ? 'Created' : 'Updated'} ${EVENT_PREFIX}`, { module: modules.reportView });

	if (!options.skipEvent) {
		broadcast(isNew ? 'added' : 'updated', data);
	}
	if (!options.skipNotification) {
		notify(isNew ? 'default.saved' : 'default.updated', isNew ? 'saved.listView' : 'updated.listView', false);
	}
};

const onSaveError = (options: ReportViewOptions) => {
	if (!options.skipErrorNotification) {
		notify('default.error', 'saveError.listView', true);
	}
};

const onDelete = (options: ReportViewOptions, data: unknown) => {
	if (!options.skipEvent) {
		broadcast('deleted', data);
	}
	if (!options.skipNotification) {
		notify('default.deleted', 'deleted.listView', false);
	}
};

const onDeleteError = (options: ReportViewOptions) => {
	if (!options.skipErrorNotification) {
		notify('default.error', 'deleteError.listView', true);
	}
};

type SaveTarget = {
	entity: string;
	visibilityType: string | null;
	id?: number | string | null;
	useOldResource: boolean;
};

type DeleteTarget = {
	entity: string | null;
	id?: number | string | null;
	useOldResource: boolean;
};

/*
	The two $resource url templates from reportView.js — neither carries a customer-id segment,
	unlike listView.js:
		Resource    = URL + API + 'reportViews/:entity/:id'
		OldResource = URL + API + 'reportview/:entity/:visibilityType/:id'
	$resource drops a :param it has no value for and appends every param the template does NOT name
	as a query string — which is why visibilityType is a query param on the new url and a path
	segment on the old one. Split as { _url, path } because Resource._getUrl joins the two with a
	slash and would otherwise leave a trailing one.
*/
const buildSaveRequest = ({ entity, visibilityType, id, useOldResource }: SaveTarget) => {
	if (useOldResource) {
		return id
			? { path: String(id), opts: { _url: `reportview/${entity}/${visibilityType}` } }
			: { path: String(visibilityType), opts: { _url: `reportview/${entity}` } };
	}

	return id
		? { path: String(id), opts: { _url: `reportViews/${entity}`, params: { visibilityType } } }
		: { path: entity, opts: { _url: 'reportViews', params: { visibilityType } } };
};

/*
	delete carries a method-level url override in reportView.js that replaces BOTH resources:
		SHARED_VIEWS on  -> 'reportViews/:entity/:id'
		SHARED_VIEWS off -> 'reportview/:id'
	so with the flag off entity is a query param rather than a path segment. Preserved verbatim.
*/
const buildDeleteRequest = ({ entity, id, useOldResource }: DeleteTarget) => {
	if (useOldResource) {
		return { path: String(id), opts: { _url: 'reportview', params: { entity } } };
	}

	return { path: String(id), opts: { _url: entity ? `reportViews/${entity}` : 'reportViews' } };
};

class ReportViewResource extends Resource {
	constructor() {
		// Every request overrides _url, but Resource requires a base url.
		// No notificationTitle: notifications are fired by the helpers above, not by the base class.
		super('reportViews');
	}

	_save(target: SaveTarget, view: ReportViewLike): Promise<ReportViewResponse> {
		const { path, opts } = buildSaveRequest(target);
		const requestOpts = { ...opts, methodName: 'save' };
		const body = stripAngularInternals(view);

		const request = target.id
			? this._putRequest(path, body, requestOpts)
			: this._postRequest(path, body, requestOpts);

		return request.then((res: { data: ReportViewResponse }) => res.data);
	}

	_delete(target: DeleteTarget): Promise<ReportViewResponse> {
		const { path, opts } = buildDeleteRequest(target);

		return this._deleteRequest(path, { ...opts, methodName: 'delete' }).then(
			(res: { data: ReportViewResponse }) => res.data
		);
	}
}

/*
	The Angular factory returned a single mutable Model, so the instance state below is a singleton
	too: setEntityType / setVisibilityType are consumed by the next call, save nulls them again and
	delete does not.
*/
class ReportViewInstance {
	private resource: ReportViewResource;
	visibilityType: string | null = null;
	entity: string | null = null;

	constructor(resource: ReportViewResource) {
		this.resource = resource;
	}

	setVisibilityType(type: string) {
		this.visibilityType = type;
		return this;
	}

	setEntityType(type: string) {
		this.entity = type;
		return this;
	}

	save(view: ReportViewLike, options: ReportViewOptions = {}): Promise<ReportViewResponse> {
		if (!this.entity) {
			throw new Error('No entity type set for list view resource');
		}
		/*
			The React callers never call setVisibilityType, so with SHARED_VIEWS off this throws for
			them today as well. Kept: removing the !SHARED_VIEWS branches is WEB-7713's job.
		*/
		const sharedViews = isAvailable(Feature.SHARED_VIEWS);

		if (!sharedViews) {
			if (!this.visibilityType) {
				throw new Error('No visibility type set for list view resource');
			}
		} else {
			this.visibilityType = 'own';
		}

		if (!view) {
			return Promise.reject('No view');
		}

		const isNew = !view.id;
		const promise = this.resource._save(
			{
				entity: this.entity,
				visibilityType: this.visibilityType,
				id: view.id,
				useOldResource: !sharedViews
			},
			view
		);

		promise
			.then(res => {
				onSave(options, res.data, isNew);
				return res;
			})
			.catch(() => {
				onSaveError(options);
			});

		this.visibilityType = null;
		this.entity = null;

		return promise;
	}

	delete(view: ReportViewLike, options: ReportViewOptions = {}): Promise<ReportViewResponse | undefined> {
		// No entity-type check and no nulling of the instance state — matching the Angular original.
		return this.resource
			._delete({
				entity: this.entity,
				id: view.id,
				useOldResource: !isAvailable(Feature.SHARED_VIEWS)
			})
			.then(res => {
				onDelete(options, view);
				return res;
			})
			.catch(() => {
				onDeleteError(options);
				return undefined;
			});
	}
}

const ReportView = new ReportViewInstance(new ReportViewResource());

/*
	Kept on the same (type, view, options) shape as saveListView / deleteListView so the call sites
	in SharedViewsReducer read the same in both branches of their isReport check. saveReportView
	resolves the saved view unwrapped, so callers no longer unwrap the envelope themselves.
*/
export const saveReportView = (type: string, view: ReportViewLike, options?: ReportViewOptions): Promise<any> =>
	ReportView.setEntityType(type)
		.save(view, options)
		.then(res => res.data);

export const deleteReportView = (
	type: string,
	view: ReportViewLike,
	options?: ReportViewOptions
): Promise<ReportViewResponse | undefined> => ReportView.setEntityType(type).delete(view, options);

export default ReportView;
